import { Alert, Stack, Typography } from '@mui/material';
import { hasEvacContent } from 'features/wildfires/utils/hasEvacContent';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { FormEvacZoneStatus } from 'shared/types';
import { removeHTMLTags } from 'shared/utils';

type DeactivationAlertProps = {
  hasEvacZones: boolean;
};

type AlertTitleProps = {
  acreage: number;
  isFps: boolean;
  manualDeactivationStarted: boolean;
};

type FormValues = {
  acreage: number | null;
  containment: number | null;
  isPrescribed: boolean;
  isFps: boolean;
  evacZoneStatuses: FormEvacZoneStatus[];
  manualDeactivationStarted: boolean | null;
  evacuationOrders: string;
  evacuationWarnings: string;
  evacuationAdvisories: string;
  evacuationNotes: string;
  customOrders: boolean;
  customWarnings: boolean;
  customAdvisories: boolean;
};

const convertAcreageToHours = (acreage: number): number => {
  if (acreage > 25) return 8;
  if (acreage > 10) return 6;
  return 4;
};

const AlertTitle = (props: AlertTitleProps): JSX.Element => {
  const { acreage, isFps, manualDeactivationStarted } = props;

  if (manualDeactivationStarted) {
    return (
      <Typography>
        <Trans i18nKey="createEditGeoEvent.deactivationStarted.manual">
          Incident deactivation timer started -
          <b>Approximately 6 hours from Manual Deactivation</b>.
        </Trans>
      </Typography>
    );
  }

  if (isFps) {
    return (
      <Typography>
        <Trans i18nKey="createEditGeoEvent.deactivationStarted.fps">
          Incident deactivation timer started -
          <b>Approximately 6 hours from FPS</b>.
        </Trans>
      </Typography>
    );
  }

  return (
    <Typography>
      <Trans
        i18nKey="createEditGeoEvent.deactivationStarted.containment"
        values={{ hours: convertAcreageToHours(acreage) }}
      >
        Incident deactivation timer started -
        <b>Approximately 4 hours from 100% containment</b>.
      </Trans>
    </Typography>
  );
};

export const WildfireDeactivationAlert = (
  props: DeactivationAlertProps,
): JSX.Element | null => {
  const { hasEvacZones } = props;
  const { t } = useTranslation();
  const { watch } = useFormContext<FormValues>();

  const [
    acreage,
    containment,
    isPrescribed,
    isFps,
    evacZoneStatuses,
    manualDeactivationStarted,
    evacuationOrders,
    evacuationWarnings,
    evacuationAdvisories,
    evacuationNotes,
    customOrders,
    customWarnings,
    customAdvisories,
  ] = watch([
    'acreage',
    'containment',
    'isPrescribed',
    'isFps',
    'evacZoneStatuses',
    'manualDeactivationStarted',
    'evacuationOrders',
    'evacuationWarnings',
    'evacuationAdvisories',
    'evacuationNotes',
    'customOrders',
    'customWarnings',
    'customAdvisories',
  ]);

  const hasEvacuationOrders = hasEvacContent({
    evacContent: evacuationOrders,
    customEvac: customOrders,
    hasEvacZones,
  });
  const hasEvacuationWarnings = hasEvacContent({
    evacContent: evacuationWarnings,
    customEvac: customWarnings,
    hasEvacZones,
  });
  const hasEvacuationAdvisories = hasEvacContent({
    evacContent: evacuationAdvisories,
    customEvac: customAdvisories,
    hasEvacZones,
  });
  const hasEvacuationNotes = Boolean(removeHTMLTags(evacuationNotes ?? ''));

  const deactivationStarted =
    !isPrescribed &&
    !evacZoneStatuses.length &&
    !hasEvacuationOrders &&
    !hasEvacuationWarnings &&
    !hasEvacuationAdvisories &&
    !hasEvacuationNotes &&
    (Boolean(manualDeactivationStarted) || isFps || containment === 100);

  if (!deactivationStarted) {
    return null;
  }

  return (
    <Alert severity="info" icon={false}>
      <Stack spacing={1}>
        <AlertTitle
          acreage={acreage || 0}
          isFps={isFps}
          manualDeactivationStarted={Boolean(manualDeactivationStarted)}
        />

        <Typography>
          <Trans i18nKey="createEditGeoEvent.deactivationStarted.silentUpdate">
            <b>Silent Update</b> will be posted at time of deactivation.
          </Trans>
        </Typography>

        <Typography fontStyle="italic">
          {t('createEditGeoEvent.deactivationStarted.deactivated')}
        </Typography>
      </Stack>
    </Alert>
  );
};
